import { createFileRoute, Link } from "@tanstack/react-router";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import { MobileNav } from "@/components/marketing/mobile-nav";
import { useAuth } from "@/hooks/use-auth";

const LOGO_LIGHT = logoLight.url;

const PRIMARY_CTA = { to: "/auth", search: { mode: "signup" as const } } as const;

const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const GOLD_2 = "#D8B260";
const GOLD_DEEP = "#A87C2E";
const INK = "#14213A";
const INK_2 = "#545C6B";
const INK_MUTED = "#6B7384";
const LINE = "#E4DECF";
const LINE_2 = "#EFEADD";
const NAVY_TEXT = "#EDEAE0";
const NAVY_TEXT_DIM = "#B6BFCD";
const NAVY_TEXT_DIM_2 = "#9AA5B4";
const NAVY_TEXT_DIM_3 = "#7E8A9C";

const FONT_SERIF = "'Spectral', Georgia, serif";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";


const FAQS: Array<[string, string]> = [
  [
    "Who can be a partner?",
    "Anyone whose work puts them next to companies that raise capital. Advisors and fractional CFOs, accountants, lawyers, brokers, consultants, software and data room vendors, accelerators and angel groups, media and newsletters, and existing customers referring a peer.",
  ],
  [
    "What exactly am I paid for?",
    "A share of CapitalDart software revenue, 30% of every purchase from a client you send. It is never tied to whether a raise closes or how large it is, and it is not compensation for introducing investors.",
  ],
  [
    "What are the two ways to be credited?",
    "Your tracked link, for anyone who clicks it, and a named referral, where you enter the company and contact in your dashboard after a conversation. Named referrals exist because most recommendations happen by conversation, not by click.",
  ],
  [
    "How long does attribution last?",
    "A named referral is credited if that company or contact signs up within 180 days of your submission. A link click is credited for 60 days. The first valid attribution wins, and a named submission that predates a click beats the click.",
  ],
  [
    "When am I paid?",
    "Monthly, on the first of the month following a completed purchase, through our partner platform. There is no cap and no minimum for named referrals recorded in your dashboard.",
  ],
  [
    "What may I say, and what may I not say?",
    "You may recommend CapitalDart as software, describe what it does, share your link and name a company you spoke to. You may never solicit investors or make representations about any offering, never promise a raise will close, and never present CapitalDart as a broker or an adviser.",
  ],
  [
    "Do I have to disclose that I am paid?",
    "Yes. One plain sentence is enough, and the FTC endorsement guidelines require it.",
  ],
  [
    "Is there a cost to join?",
    "No. Partners also receive complimentary access to CapitalDart so you can use the product before recommending it.",
  ],
];

const EARNINGS_ROWS: Array<[string, string, string]> = [
  ["Standard plan", "$500", "$150"],
  ["Three clients in a year", "$1,500", "$450"],
  ["One client a month", "$6,000", "$1,800"],
];

const PARTNER_TYPES: Array<[string, string]> = [
  ["Advisors and fractional CFOs", "You sit inside the raise already."],
  ["Accountants and lawyers", "Your clients ask you who to talk to."],
  ["Brokers and consultants", "You are asked for tools, not only advice."],
  ["Software and data room vendors", "Your users are raising while they use you."],
  ["Accelerators and angel groups", "Every cohort needs targeting."],
  ["Media and newsletters", "Your readers are founders and sponsors."],
  ["Customers referring peers", "The recommendation that converts best."],
];

const HOW_STEPS = [
  {
    n: "01",
    title: "Apply and get approved",
    body: "One short application. We review it by hand and reply by email.",
  },
  {
    n: "02",
    title: "Refer by link or by name",
    body: "Share your tracked link, or name the company and contact you spoke to in your dashboard.",
  },
  {
    n: "03",
    title: "Earn 30% of every purchase",
    body: "$150 on a standard plan, paid monthly. No cap. Software revenue only.",
  },
];

export const Route = createFileRoute("/partners")({
  head: () => ({
    meta: [
      { title: "CapitalDart Partner Program" },
      {
        name: "description",
        content:
          "Earn 30% of every CapitalDart purchase you send. Open to advisors and fractional CFOs, accountants, lawyers, brokers, consultants, vendors, accelerators, newsletters and customers referring peers.",
      },
      {
        property: "og:title",
        content: "CapitalDart Partner Program",
      },
      {
        property: "og:description",
        content:
          "Earn 30% of every CapitalDart purchase you send. Open to advisors and fractional CFOs, accountants, lawyers, brokers, consultants, vendors, accelerators, newsletters and customers referring peers.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "CapitalDart Partner Program",
      },
      {
        name: "twitter:description",
        content:
          "Earn 30% of every CapitalDart purchase you send. Open to advisors and fractional CFOs, accountants, lawyers, brokers, consultants, vendors, accelerators, newsletters and customers referring peers.",
      },
    ],
  }),
  component: PartnersPage,
});

function PartnersPage() {
  return (
    <main
      id="top"
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased overflow-x-clip"
    >
      <style>{`
        .cd-supademo-container { width: 100%; max-width: 1200px; margin: 0 auto; padding: 0 16px; overflow: visible; }
        @media (min-width: 1024px) { .cd-supademo-container { padding: 0 32px; } }
        .cd-supademo-embed { position: relative; box-sizing: border-box; width: 100%; aspect-ratio: 1.60; max-height: 80vh; max-height: 80svh; padding: 40px 0; overflow: visible; }
        @media (max-width: 1023.98px) { .cd-supademo-embed { padding: 0; max-height: 70vh; max-height: 70svh; } }
        .cd-partners-faq-row > summary { list-style: none; cursor: pointer; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
        .cd-partners-faq-row > summary::-webkit-details-marker { display: none; }
        .cd-partners-faq-row > .cd-partners-faq-answer { margin-top: 12px; }
        .cd-partners-faq-row .cd-partners-faq-chevron { transition: transform .2s ease; flex: 0 0 auto; margin-top: 4px; color: ${GOLD}; }
        .cd-partners-faq-row[open] .cd-partners-faq-chevron { transform: rotate(180deg); }
        @media (max-width: 1023.98px) { .cd-partners-nav-mobile { display: inline-flex !important; } }
      `}</style>
      <Hero />
      <WhoItsFor />
      <HowItWorks />
      <Earnings />
      <WhyConverts />
      <SupademoDemo />
      <Faq />
      <FinalCta />
      <LandingFooter />
    </main>
  );
}

function LandingLogo({ height = 32 }: { height?: number }) {
  return (
    <Logo
      src={LOGO_LIGHT}
      alt="CapitalDart"
      height={height}
      style={{ display: "block" }}
      trademarkClassName="text-[#F4F1E8]"
    />
  );
}

function GoldButton({
  children,
  to,
  href,
  search,
  style,
  className,
}: {
  children: React.ReactNode;
  to?: "/auth" | "/partners/apply";
  href?: string;
  search?: { mode: "signin" | "signup" };
  style?: React.CSSProperties;
  className?: string;
}) {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: FONT_SANS,
    fontWeight: 600,
    color: NAVY,
    background: GOLD,
    borderRadius: 2,
    letterSpacing: ".01em",
    textDecoration: "none",
    transition: "background .15s",
    ...style,
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD_2;
  };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD;
  };
  if (to) {
    return (
      <Link
        to={to}
        search={search as never}
        className={className}
        style={base}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      className={className}
      style={base}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

function Kicker({
  children,
  color = INK_MUTED,
  style,
}: {
  children: React.ReactNode;
  color?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: 12,
        letterSpacing: ".2em",
        textTransform: "uppercase",
        color,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Hero() {
  const { session, loading } = useAuth();
  const isAuthed = Boolean(session);
  return (
    <section
      style={{
        background: NAVY,
        backgroundImage: `radial-gradient(120% 80% at 85% -10%, rgba(201,162,75,.12), rgba(11,37,69,0) 60%)`,
        color: NAVY_TEXT,
        position: "relative",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)",
          backgroundSize: "64px 64px",
          pointerEvents: "none",
        }}
      />
      <nav
        className="mx-auto"
        style={{
          maxWidth: 1200,
          position: "relative",
          padding: "26px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        <Link to="/" hash="top" style={{ textDecoration: "none" }}>
          <LandingLogo height={34} />
        </Link>
        <div className="hidden lg:flex" style={{ alignItems: "center", gap: 32 }}>
          {[
            ["Platform", "/#how"],
            ["Pricing", "/#pricing"],
            ["FAQ", "/#faq"],
          ].map(([label, href]) => (
            <a
              key={href}
              href={href}
              style={{
                color: "#AEB8C7",
                fontSize: 14.5,
                fontFamily: FONT_SANS,
                textDecoration: "none",
                transition: "color .15s",
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = PAPER)}
              onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "#AEB8C7")}
            >
              {label}
            </a>
          ))}
          <Link
            to="/contact"
            style={{
              color: "#AEB8C7",
              fontSize: 14.5,
              fontFamily: FONT_SANS,
              textDecoration: "none",
              transition: "color .15s",
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = PAPER)}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "#AEB8C7")}
          >
            Contact
          </Link>
        </div>
        <div className="hidden lg:flex" style={{ alignItems: "center", gap: 18 }}>
          {loading ? null : isAuthed ? (
            <Link
              to="/dashboard"
              style={{
                fontSize: 14.5,
                fontWeight: 600,
                color: NAVY,
                background: GOLD,
                padding: "11px 20px",
                borderRadius: 2,
                textDecoration: "none",
                fontFamily: FONT_SANS,
              }}
            >
              Dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/auth"
                search={{ mode: "signin" }}
                style={{
                  color: "#AEB8C7",
                  fontSize: 14.5,
                  fontFamily: FONT_SANS,
                  textDecoration: "none",
                  transition: "color .15s",
                }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = PAPER)}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "#AEB8C7")}
              >
                Sign in
              </Link>
              <GoldButton
                to={PRIMARY_CTA.to}
                search={PRIMARY_CTA.search}
                style={{ fontSize: 14.5, padding: "11px 20px" }}
              >
                Get started
              </GoldButton>
            </>
          )}
        </div>
        <div className="lg:hidden">
          <MobileNav className="cd-partners-nav-mobile" />
        </div>
      </nav>

      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{ maxWidth: 1200, position: "relative", padding: "80px 0 120px" }}
      >
        <div className="max-w-3xl">
          <Kicker color={GOLD} style={{ marginBottom: 22 }}>
            Partner Program
          </Kicker>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(40px,5vw,62px)",
              lineHeight: 1.04,
              letterSpacing: "-.015em",
              margin: "0 0 20px",
              color: "#F7F5EE",
            }}
          >
            Get paid for the companies you already send our way
          </h1>
          <p
            style={{
              fontSize: 18,
              lineHeight: 1.6,
              color: NAVY_TEXT_DIM,
              maxWidth: 640,
              margin: "0 0 40px",
            }}
          >
            Open to anyone who works with companies that raise capital. You earn 30% of every
            CapitalDart purchase you send, by link or by naming the company you spoke to.
            Compensation is a share of software revenue only. It is never tied to whether a raise
            closes or how large it is.
          </p>
          <GoldButton
            to="/partners/apply"
            style={{ display: "inline-flex", fontSize: 16, padding: "16px 34px" }}
          >
            Apply to partner
          </GoldButton>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1200 }}>
        <div style={{ marginBottom: 56 }}>
          <Kicker style={{ marginBottom: 18 }}>How it works</Kicker>
          <h2
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(32px,3.6vw,46px)",
              lineHeight: 1.06,
              letterSpacing: "-.01em",
              margin: 0,
            }}
          >
            Three steps to partner revenue.
          </h2>
        </div>
        <div
          className="grid gap-6 md:grid-cols-3"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}
        >
          {HOW_STEPS.map((step) => (
            <div
              key={step.n}
              style={{
                background: PAPER_2,
                border: `1px solid ${LINE}`,
                borderRadius: 5,
                padding: "34px 30px",
              }}
            >
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 13,
                  letterSpacing: ".1em",
                  color: GOLD,
                  marginBottom: 14,
                }}
              >
                STEP {step.n}
              </div>
              <h3
                style={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 600,
                  fontSize: 22,
                  lineHeight: 1.2,
                  margin: "0 0 12px",
                }}
              >
                {step.title}
              </h3>
              <p style={{ fontSize: 15.5, lineHeight: 1.6, color: INK_2, margin: 0 }}>
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhoItsFor() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER_2, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1200 }}>
        <Kicker style={{ marginBottom: 18 }}>Who partners with us</Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(32px,3.6vw,46px)",
            lineHeight: 1.06,
            letterSpacing: "-.01em",
            margin: "0 0 18px",
          }}
        >
          Open to anyone, not only creators.
        </h2>
        <p style={{ fontSize: 16.5, lineHeight: 1.65, color: INK_2, maxWidth: 720, margin: "0 0 44px" }}>
          You do not need an audience. Most CapitalDart referrals happen in conversation, so a
          single well placed recommendation counts exactly as much as a link click.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 18,
          }}
        >
          {PARTNER_TYPES.map(([title, body]) => (
            <div
              key={title}
              style={{
                background: PAPER,
                border: `1px solid ${LINE}`,
                borderRadius: 5,
                padding: "24px 22px",
              }}
            >
              <div style={{ fontFamily: FONT_SERIF, fontWeight: 600, fontSize: 18.5, marginBottom: 8 }}>
                {title}
              </div>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: INK_2, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Earnings() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER_2, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1000 }}>
        <Kicker style={{ marginBottom: 18 }}>What you earn</Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(32px,3.6vw,46px)",
            lineHeight: 1.06,
            letterSpacing: "-.01em",
            margin: "0 0 32px",
          }}
        >
          30% of every purchase you send.
        </h2>
        <div style={{ overflowX: "auto", border: `1px solid ${LINE}`, borderRadius: 5, background: PAPER }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15.5 }}>
            <thead>
              <tr style={{ background: PAPER_2, textAlign: "left" }}>
                <th style={{ padding: "14px 18px", fontFamily: FONT_MONO, fontSize: 12, letterSpacing: ".08em", color: INK_MUTED, textTransform: "uppercase" }}>
                  What you refer
                </th>
                <th style={{ padding: "14px 18px", fontFamily: FONT_MONO, fontSize: 12, letterSpacing: ".08em", color: INK_MUTED, textTransform: "uppercase" }}>
                  Client pays
                </th>
                <th style={{ padding: "14px 18px", fontFamily: FONT_MONO, fontSize: 12, letterSpacing: ".08em", color: INK_MUTED, textTransform: "uppercase" }}>
                  You earn
                </th>
              </tr>
            </thead>
            <tbody>
              {EARNINGS_ROWS.map(([label, paid, earn]) => (
                <tr key={label} style={{ borderTop: `1px solid ${LINE_2}` }}>
                  <td style={{ padding: "16px 18px" }}>{label}</td>
                  <td style={{ padding: "16px 18px", color: INK_2 }}>{paid}</td>
                  <td style={{ padding: "16px 18px", fontWeight: 600, color: GOLD_DEEP }}>{earn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 18,
            marginTop: 28,
          }}
        >
          <div style={{ background: PAPER, border: `1px solid ${LINE}`, borderRadius: 5, padding: "24px 22px" }}>
            <div style={{ fontFamily: FONT_SERIF, fontWeight: 600, fontSize: 19, marginBottom: 8 }}>
              Credited by link
            </div>
            <p style={{ fontSize: 14.5, lineHeight: 1.6, color: INK_2, margin: 0 }}>
              Share your tracked link. Anyone who clicks it is credited to you for 60 days.
            </p>
          </div>
          <div style={{ background: PAPER, border: `1px solid ${LINE}`, borderRadius: 5, padding: "24px 22px" }}>
            <div style={{ fontFamily: FONT_SERIF, fontWeight: 600, fontSize: 19, marginBottom: 8 }}>
              Credited by name
            </div>
            <p style={{ fontSize: 14.5, lineHeight: 1.6, color: INK_2, margin: 0 }}>
              After a conversation, enter the company and contact in your dashboard. If they sign
              up within 180 days you are credited, whatever route they came by.
            </p>
          </div>
        </div>
        <p style={{ fontSize: 13.5, lineHeight: 1.65, color: INK_MUTED, marginTop: 24, maxWidth: 760 }}>
          Compensation is a share of CapitalDart software revenue only. It is never tied to whether
          a raise closes or how large it is. A partner never solicits investors and never makes
          representations about any offering.
        </p>
      </div>
    </section>
  );
}

function WhyConverts() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1000 }}>
        <Kicker style={{ marginBottom: 18 }}>Why CapitalDart converts</Kicker>
        <p
          style={{
            fontFamily: FONT_SERIF,
            fontSize: "clamp(22px,2.4vw,30px)",
            lineHeight: 1.35,
            margin: 0,
            color: INK,
          }}
        >
          CapitalDart is an AI platform that matches companies, funds, and real estate sponsors with
          the right investors, down to the specific person at each firm and that person's individual
          investment criteria. It drafts the outreach and tracks the pipeline. Built on data from
          thousands of real capital raises.
        </p>
      </div>
    </section>
  );
}

function SupademoDemo() {
  return (
    <section style={{ background: NAVY, color: NAVY_TEXT, padding: "96px 0" }}>
      <div className="cd-supademo-container">
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <Kicker color={GOLD}>Interactive demo</Kicker>
          <h2
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(28px,3.2vw,40px)",
              lineHeight: 1.1,
              letterSpacing: "-.01em",
              margin: "12px 0 0",
              color: "#F7F5EE",
            }}
          >
            See how it works
          </h2>
        </div>
        <div
          className="cd-supademo-embed"
          dangerouslySetInnerHTML={{
            __html:
              '<iframe src="https://app.supademo.com/embed/cmr3k5acy0047z90j0ev0p2ya?embed_v=2&utm_source=embed" loading="lazy" title="CapitalDart" allow="clipboard-write" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe>',
          }}
        />
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1000 }}>
        <div style={{ marginBottom: 48 }}>
          <Kicker style={{ marginBottom: 18 }}>FAQ</Kicker>
          <h2
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(32px,3.6vw,46px)",
              lineHeight: 1.06,
              letterSpacing: "-.01em",
              margin: 0,
            }}
          >
            Partner questions.
          </h2>
        </div>
        <div style={{ borderTop: `1px solid ${LINE}` }}>
          {FAQS.map(([q, a], i) => (
            <details
              key={q}
              className="cd-partners-faq-row"
              style={{
                padding: "28px 0",
                borderBottom: i === FAQS.length - 1 ? "none" : `1px solid ${LINE_2}`,
              }}
            >
              <summary>
                <h3
                  style={{
                    fontFamily: FONT_SERIF,
                    fontWeight: 600,
                    fontSize: 19,
                    lineHeight: 1.25,
                    margin: 0,
                  }}
                >
                  {q}
                </h3>
                <svg
                  className="cd-partners-faq-chevron"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </summary>
              <p
                className="cd-partners-faq-answer"
                style={{ fontSize: 15, lineHeight: 1.65, color: INK_2, margin: 0 }}
              >
                {a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      style={{ background: NAVY, color: NAVY_TEXT, padding: "96px 0", position: "relative" }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(90% 70% at 50% 120%, rgba(201,162,75,.14), rgba(11,37,69,0) 60%)`,
          pointerEvents: "none",
        }}
      />
      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{ position: "relative", maxWidth: 900, textAlign: "center" }}
      >
        <Kicker color={GOLD} style={{ marginBottom: 22 }}>
          Get started
        </Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(36px,4.6vw,60px)",
            lineHeight: 1.04,
            letterSpacing: "-.015em",
            margin: "0 0 22px",
            color: "#F7F5EE",
          }}
        >
          Become a CapitalDart partner
        </h2>
        <p
          style={{
            fontSize: 17,
            lineHeight: 1.6,
            color: NAVY_TEXT_DIM,
            maxWidth: 540,
            margin: "0 auto 40px",
          }}
        >
          One application. Your link, your named referral form and a hub of copy you can use as it
          is, all in one dashboard.
        </p>
        <GoldButton
          to="/partners/apply"
          style={{ display: "inline-flex", fontSize: 16, padding: "16px 34px", marginTop: 4 }}
        >
          Apply to partner
        </GoldButton>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer style={{ background: NAVY_DEEP, color: NAVY_TEXT_DIM_2, padding: "48px 0" }}>
      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{
          maxWidth: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LandingLogo height={26} />
          <span style={{ fontSize: 13.5, color: "#56627A", marginLeft: 14, fontFamily: FONT_SANS }}>
            © 2026
          </span>
        </div>
        <div style={{ display: "flex", gap: 26, fontSize: 14, fontFamily: FONT_SANS }}>
          <Link to="/help" style={{ color: "inherit", textDecoration: "none" }}>
            Help
          </Link>
          <Link to="/privacy" style={{ color: "inherit", textDecoration: "none" }}>
            Privacy
          </Link>
          <Link to="/terms" style={{ color: "inherit", textDecoration: "none" }}>
            Terms
          </Link>
          <Link to="/connect" style={{ color: "inherit", textDecoration: "none" }}>
            Use with Claude / ChatGPT
          </Link>
          <Link to="/connect/capabilities" style={{ color: "inherit", textDecoration: "none" }}>
            Claude / ChatGPT capabilities
          </Link>
          <Link to="/partners" style={{ color: "inherit", textDecoration: "none" }}>
            Partners
          </Link>
          <Link to="/contact" style={{ color: "inherit", textDecoration: "none" }}>
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}
